import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Grid,
  TextField,
  Pagination,
  FormControl,
  MenuItem,
  Typography,
  Avatar as MuiAvatar,
} from "@mui/material";
import { Breadcrumb, SimpleCard } from "app/components";
import { DeleteOutline, Edit as EditIcon, Visibility } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Loading from "app/components/MatxLoading";
import Avatar from "react-avatar";
import { PAGE_LIMIT, PATH, LIVE_URL } from "../../../config";
import { StyledTable } from "app/assets";
import { toast } from "react-toastify";
import { GETALLUSERS, REMOVEUSER } from "app/redux/actions";
import styled from "styled-components";

const TableContainer = styled.div`
  overflow-x: auto;
  @media (max-width: 600px) {
    width: 150%;
    border: 1px solid #e0e0e0;
  }
`;

const AllEmployees = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const loggedInUser = useSelector((state) => state.user.user);
  const user = useSelector((state) => state.user);

  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [status, setStatus] = useState("active");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);

  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [viewUser, setViewUser] = useState(null);

  useEffect(() => {
    if (loggedInUser?.token) {
      dispatch(
        GETALLUSERS(
          {
            page: page,
            limit: PAGE_LIMIT,
            searchValue: searchValue,
            status: status === "terminated" ? null : status,
            isTerminated: status === "terminated" ? true : false,
          },
          loggedInUser
        )
      );
    }
  }, [dispatch, loggedInUser, page, searchValue, status]);

  const handlePageChange = (event, value) => setPage(value);
  const handleSearch = () => setPage(1);

  const onSuccess = (message) => toast.success(message);
  const onFail = (message) => toast.error(message);

  const handleDeleteClick = (id) => {
    setEmployeeToDelete(id);
    setDialogOpen(true);
  };

  const confirmDelete = () => {
    dispatch(REMOVEUSER({ permanent: false }, employeeToDelete, loggedInUser, onSuccess, onFail));
    setDialogOpen(false);
    setEmployeeToDelete(null);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEmployeeToDelete(null);
  };

  const handleViewUser = (userData) => {
    setViewUser(userData);
    setOpenViewDialog(true);
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setViewUser(null);
  };

  const handleEdit = (employeeId) => {
    navigate(`${PATH.EDITEMPLOYEE.replace(":id", employeeId)}`);
  };

  return (
    <SimpleCard>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Breadcrumb routeSegments={[{ name: "All Employees" }]} />
        <Button variant="contained" onClick={() => navigate(PATH.CREATEEMPLOYEE)}>
          Create Employee
        </Button>
      </Box>

      {/* FILTERS */}
      <Grid container spacing={3} sx={{ my: 2 }}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <TextField
              fullWidth
              label="Search by Name"
              variant="outlined"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <TextField
              select
              name="status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              label="Filter by Type"
            >
              <MenuItem value=""><em>All</em></MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="terminated">Terminated</MenuItem>
            </TextField>
          </FormControl>
        </Grid>
      </Grid>

      {/* TABLE */}
      {user.allUsersLoading ? (
        <Loading />
      ) : (
        <>
          <Box sx={{ flexGrow: 1, overflow: "auto", paddingBottom: "70px" }}>
            <TableContainer>
              <StyledTable>
                <TableHead>
                  <TableRow>
                    <TableCell align="center">Profile</TableCell>
                    <TableCell align="left">Name</TableCell>
                    <TableCell align="left">Email</TableCell>
                    <TableCell align="center">Device ID</TableCell>
                    <TableCell align="center">Joining Date</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Role</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!user?.allUsers?.users || user?.allUsers?.users.length === 0 ? (
                    <TableCell colSpan={9} align="center">No Employees Found</TableCell>
                  ) : (
                    user?.allUsers?.users.map((userData) => (
                      <TableRow key={userData.id}>
                        <TableCell align="center">
                          <Avatar size="40" round={true} name={userData.name} src={userData.avatar ? `${LIVE_URL}public/${userData.avatar}` : null} />
                        </TableCell>
                        <TableCell align="left">{userData.name}</TableCell>
                        <TableCell align="left">{userData.email}</TableCell>
                        <TableCell align="center">{userData.deviceId || "N/A"}</TableCell>
                        <TableCell align="center">{new Date(userData.joiningDate).toLocaleDateString()}</TableCell>
                        <TableCell align="center">{userData.status}</TableCell>
                        <TableCell align="center">{userData.userType}</TableCell>

                        <TableCell align="center">
                          {!user.removeLoading && (
                            <>
                              <IconButton onClick={() => handleEdit(userData.id)} color="primary">
                                <EditIcon />
                              </IconButton>

                              <IconButton color="error" onClick={() => handleDeleteClick(userData.id)}>
                                <DeleteOutline />
                              </IconButton>

                              <IconButton onClick={() => handleViewUser(userData)} color="primary">
                                <Visibility />
                              </IconButton>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </StyledTable>
            </TableContainer>
          </Box>
          <Box display="flex" justifyContent="center" sx={{ position: "sticky", bottom: 0, py: 2, backgroundColor: "background.paper", zIndex: 1, boxShadow: "0 -2px 5px rgba(0,0,0,0.1)" }}>
            <Pagination count={user?.allUsers?.totalPages || 1} page={page} onChange={handlePageChange} color="primary" />
          </Box>
        </>
      )}

      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete this employee?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="primary">Cancel</Button>
          <Button onClick={confirmDelete} color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openViewDialog}
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Employee Details</DialogTitle>
        <DialogContent dividers>
          {viewUser && (
            <Box sx={{ p: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                <MuiAvatar
                  src={viewUser.avatar ? `${LIVE_URL}public/${viewUser.avatar}` : ""}
                  sx={{ width: 100, height: 100, border: "2px solid #ddd" }}
                />
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">Name</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>{viewUser.name}</Typography>

                  <Typography variant="subtitle2" color="textSecondary">Email</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>{viewUser.email}</Typography>

                  <Typography variant="subtitle2" color="textSecondary">Phone</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>{viewUser.phone}</Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">CNIC</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>{viewUser.cnic}</Typography>

                  <Typography variant="subtitle2" color="textSecondary">Role</Typography>
                  <Typography variant="body1" sx={{ mb: 2, textTransform: 'capitalize' }}>{viewUser.userType}</Typography>

                  <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      mb: 2,
                      color: viewUser.status === 'active' ? 'green' : 'red',
                      fontWeight: 'bold',
                      textTransform: 'capitalize'
                    }}
                  >
                    {viewUser.status}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">Joining Date</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {viewUser.joiningDate ? new Date(viewUser.joiningDate).toLocaleDateString() : 'N/A'}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">Device ID</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>{viewUser.deviceId || 'N/A'}</Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography sx={{ mb: 1 }} variant="subtitle2" color="textSecondary">Account Number / Details</Typography>

                  <Box sx={{ bgcolor: "#f5f5f5", p: 2, borderRadius: 1, border: '1px solid #e0e0e0' }}>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
                      {viewUser.account_no || "No details provided"}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>CNIC Front</Typography>
                  {viewUser.cnic_front ? (
                    <img
                      src={`${LIVE_URL}public${viewUser.cnic_front}`}
                      alt="CNIC Front"
                      style={{ width: "100%", maxHeight: "200px", objectFit: "contain", border: "1px solid #eee", borderRadius: "4px" }}
                    />
                  ) : (
                    <Typography variant="body2" color="error">Not Uploaded</Typography>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>CNIC Back</Typography>
                  {viewUser.cnic_back ? (
                    <img
                      src={`${LIVE_URL}public${viewUser.cnic_back}`}
                      alt="CNIC Back"
                      style={{ width: "100%", maxHeight: "200px", objectFit: "contain", border: "1px solid #eee", borderRadius: "4px" }}
                    />
                  ) : (
                    <Typography variant="body2" color="error">Not Uploaded</Typography>
                  )}
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog} variant="outlined" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </SimpleCard>
  );
};

export default AllEmployees;